import React, { useState } from "react";
import AsyncSelect from "react-select/async";
import toast from "react-hot-toast";
import { useLazySearchEmployeesQuery } from "../employeesApi";

const toOption = (emp) => ({
  value: emp.recordGuid,
  // No branch shown here - the employee search result's branchCode field is
  // the bank branch code, not the sync/org branch, so displaying it is
  // misleading rather than useful.
  label: `${emp.empName} ${emp.empLastName || ""} · ${emp.empCode}`,
  employee: emp,
});

// query must be at least 1 char (handoff §7) - react-select's own
// debounce/minLength via loadOptions keeps us from firing on empty input.
// A dedicated Employee Code field sits above the name search: type the code
// and hit Enter to jump straight to that employee, no dropdown interaction
// needed - faster than the name search for anyone who already knows the code.
const EmployeePicker = ({ value, onChange }) => {
  const [triggerSearch] = useLazySearchEmployeesQuery();
  const [empCode, setEmpCode] = useState("");
  const [isSearchingCode, setIsSearchingCode] = useState(false);

  const loadOptions = async (inputValue) => {
    if (!inputValue || inputValue.trim().length === 0) return [];
    const { data = [] } = await triggerSearch(inputValue.trim());
    return data.map(toOption);
  };

  const handleCodeSearch = async () => {
    const code = empCode.trim();
    if (!code) return;
    setIsSearchingCode(true);
    try {
      const { data = [] } = await triggerSearch(code);
      const match = data.find((emp) => String(emp.empCode) === code) || data[0];
      if (match) {
        onChange(toOption(match));
        setEmpCode("");
      } else {
        toast.error(`No employee found for code ${code}`);
      }
    } finally {
      setIsSearchingCode(false);
    }
  };

  return (
    <div>
      <div className="d-flex gap-2 mb-2">
        <input
          type="text"
          className="form-control"
          placeholder="Employee code, then Enter"
          style={{ maxWidth: 220 }}
          value={empCode}
          onChange={(e) => setEmpCode(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              handleCodeSearch();
            }
          }}
          disabled={isSearchingCode}
        />
        <button
          type="button"
          className="btn btn-outline-primary"
          onClick={handleCodeSearch}
          disabled={isSearchingCode || !empCode.trim()}
        >
          {isSearchingCode ? "Searching..." : "Find"}
        </button>
      </div>
      <AsyncSelect
        classNamePrefix="react-select"
        cacheOptions
        loadOptions={loadOptions}
        value={value}
        onChange={onChange}
        placeholder="...or search by name"
        noOptionsMessage={({ inputValue }) =>
          inputValue ? "No matching employees" : "Type to search employees"
        }
      />
    </div>
  );
};

export default EmployeePicker;
